import {
  darkPalette,
  lightPalette,
  tokenToCssValue,
  type ColorToken,
  type WidgetPalette,
  type WidgetScheme,
} from './model';

export interface RgbColor {
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

export function hexToRgb(hex: string): RgbColor | null {
  const value = hex.trim().toLowerCase();
  const short = /^#([0-9a-f])([0-9a-f])([0-9a-f])$/.exec(value);
  if (short) {
    return {
      r: Number.parseInt(short[1] + short[1], 16),
      g: Number.parseInt(short[2] + short[2], 16),
      b: Number.parseInt(short[3] + short[3], 16),
    };
  }
  const full = /^#([0-9a-f]{2})([0-9a-f]{2})([0-9a-f]{2})$/.exec(value);
  if (!full) return null;
  return {
    r: Number.parseInt(full[1], 16),
    g: Number.parseInt(full[2], 16),
    b: Number.parseInt(full[3], 16),
  };
}

export function rgbToHex({ r, g, b }: RgbColor): string {
  return `#${[r, g, b]
    .map((channel) => Math.round(channel).toString(16).padStart(2, '0'))
    .join('')}`;
}

function linearChannel(channel: number): number {
  const value = channel / 255;
  return value <= 0.04045 ? value / 12.92 : ((value + 0.055) / 1.055) ** 2.4;
}

export function relativeLuminance(color: RgbColor): number {
  return 0.2126 * linearChannel(color.r)
    + 0.7152 * linearChannel(color.g)
    + 0.0722 * linearChannel(color.b);
}

export function contrastRatio(foreground: string, background: string): number {
  const fg = hexToRgb(foreground);
  const bg = hexToRgb(background);
  if (!fg || !bg) throw new Error('contrastRatio accepts opaque 3/6-digit hexadecimal colors');
  const a = relativeLuminance(fg);
  const b = relativeLuminance(bg);
  const high = Math.max(a, b);
  const low = Math.min(a, b);
  return (high + 0.05) / (low + 0.05);
}

export function mixColors(foreground: string, background: string, weight: number): string {
  const fg = hexToRgb(foreground);
  const bg = hexToRgb(background);
  if (!fg || !bg || !Number.isFinite(weight) || weight < 0 || weight > 1) {
    throw new Error('mixColors requires opaque hex colors and a weight from 0 through 1');
  }
  return rgbToHex({
    r: foregroundChannel(fg.r, bg.r, weight),
    g: foregroundChannel(fg.g, bg.g, weight),
    b: foregroundChannel(fg.b, bg.b, weight),
  });
}

function foregroundChannel(foreground: number, background: number, weight: number): number {
  return foreground * weight + background * (1 - weight);
}

/** Composite the bounded overlay rgba syntax over its worst-case white image pixel. */
function overlayCompositeOverWhite(value: string): string {
  const match = /^rgba\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*,\s*(0(?:\.\d+)?|1(?:\.0+)?)\s*\)$/i.exec(value);
  if (!match) throw new Error(`invalid canonical overlay color: ${value}`);
  const alpha = Number(match[4]);
  return rgbToHex({
    r: Number(match[1]) * alpha + 255 * (1 - alpha),
    g: Number(match[2]) * alpha + 255 * (1 - alpha),
    b: Number(match[3]) * alpha + 255 * (1 - alpha),
  });
}

export type ContrastRole = 'normal-text' | 'large-text' | 'ui-boundary';

export interface TokenContrastPairing {
  readonly scheme: WidgetScheme;
  readonly name: string;
  readonly role: ContrastRole;
  readonly foreground: string;
  readonly background: string;
  readonly minimum: 3 | 4.5;
}

function palettePairings(scheme: WidgetScheme, palette: WidgetPalette): TokenContrastPairing[] {
  const color = (value: ColorToken): string => tokenToCssValue(value);
  const normal = (name: string, foreground: string, background: string): TokenContrastPairing => ({
    scheme, name, role: 'normal-text', foreground, background, minimum: 4.5,
  });
  const ui = (name: string, foreground: string, background: string): TokenContrastPairing => ({
    scheme, name, role: 'ui-boundary', foreground, background, minimum: 3,
  });
  return [
    normal('primary text / surface', color(palette.text), color(palette.surface)),
    normal('primary text / subtle surface', color(palette.text), color(palette.surfaceSubtle)),
    normal('secondary text / surface', color(palette.textSecondary), color(palette.surface)),
    normal('secondary text / subtle surface', color(palette.textSecondary), color(palette.surfaceSubtle)),
    normal('muted text / surface', color(palette.textMuted), color(palette.surface)),
    normal('muted text / subtle surface', color(palette.textMuted), color(palette.surfaceSubtle)),
    normal('primary text / info surface', color(palette.text), color(palette.status.info.surface)),
    normal('secondary text / info surface', color(palette.textSecondary), color(palette.status.info.surface)),
    normal('muted text / info surface', color(palette.textMuted), color(palette.status.info.surface)),
    normal('accent text / info surface', color(palette.accent), color(palette.status.info.surface)),
    normal('secondary text / danger surface', color(palette.textSecondary), color(palette.status.danger.surface)),
    normal('muted text / danger surface', color(palette.textMuted), color(palette.status.danger.surface)),
    normal('on-accent text / accent', color(palette.onAccent), color(palette.accent)),
    normal('on-accent text / accent hover', color(palette.onAccent), color(palette.accentHover)),
    normal('on-accent text / danger action', color(palette.onAccent), color(palette.status.danger.text)),
    normal('overlay text / overlay control', color(palette.overlayText), overlayCompositeOverWhite(color(palette.overlayControl))),
    normal('overlay text / hovered overlay control', color(palette.overlayText), overlayCompositeOverWhite(color(palette.overlayControlHover))),
    normal('overlay text / overlay caption', color(palette.overlayText), overlayCompositeOverWhite(color(palette.overlayCaption))),
    { scheme, name: 'large heading / subtle surface', role: 'large-text', foreground: color(palette.text), background: color(palette.surfaceSubtle), minimum: 3 },
    normal('success text / success surface', color(palette.status.success.text), color(palette.status.success.surface)),
    normal('warning text / warning surface', color(palette.status.warning.text), color(palette.status.warning.surface)),
    normal('danger text / danger surface', color(palette.status.danger.text), color(palette.status.danger.surface)),
    normal('info text / info surface', color(palette.status.info.text), color(palette.status.info.surface)),
    normal('accent text / surface', color(palette.accent), color(palette.surface)),
    ui('accent / progress track', color(palette.accent), color(palette.surfaceSubtle)),
    ui('focus / surface', color(palette.focus), color(palette.surface)),
    ui('focus / subtle surface', color(palette.focus), color(palette.surfaceSubtle)),
    ui('border / surface', color(palette.border), color(palette.surface)),
    ui('success border / page', color(palette.status.success.border), color(palette.page)),
    ui('success border / surface', color(palette.status.success.border), color(palette.surface)),
    ui('success border / success surface', color(palette.status.success.border), color(palette.status.success.surface)),
    ui('warning border / page', color(palette.status.warning.border), color(palette.page)),
    ui('warning border / warning surface', color(palette.status.warning.border), color(palette.status.warning.surface)),
    ui('danger border / page', color(palette.status.danger.border), color(palette.page)),
    ui('danger border / danger surface', color(palette.status.danger.border), color(palette.status.danger.surface)),
    ui('info border / page', color(palette.status.info.border), color(palette.page)),
    ui('info border / info surface', color(palette.status.info.border), color(palette.status.info.surface)),
  ];
}

export const TOKEN_CONTRAST_PAIRINGS = Object.freeze([
  ...palettePairings('light', lightPalette),
  ...palettePairings('dark', darkPalette),
]);

/** Slice 1 introduces no palette debt; adding an entry requires explicit review. */
export const KNOWN_TOKEN_CONTRAST_FAILURES = Object.freeze([] as const);
